import PropTypes from "prop-types"
import { UserInfos } from "components/UserInfos"
import { TabList, Tab, Tabs } from "components/Tab"
import { useRouter } from "components/Router"
import { Wrapper } from "components/Wrapper"
import { Page } from "components/Page"
import { Button } from "components/Button"
import { ConfirmButton } from "components/ConfirmButton"
import { useTranslator } from "components/Translator"
import { UserTimes } from "components/Icon/UserTimes"
import * as React from "react"
import { Modal, ModalContent } from "components/Modal"
import { Text } from "components/Text"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { TwoColLayout } from "components/TwoColLayout"
import { TrashAlt } from "components/Icon/TrashAlt"
import {
  EllipsisH,
  Envelope,
  Gift,
  Pen,
  Crown,
  SuperNavigator,
  CartPlus,
  ArrowLeft,
  CalendarCheckRegular,
  Comments,
  PlusSquareRegular,
} from "components/Icon"
import { Publicity } from "components/Publicity"
import { RequireClub } from "components/RequireClub"
import { ContactTypeIcon } from "domains/contact/ContactTypeIcon"
import { getContactBetweenUsers } from "domains/user/model"
import * as BookingStatus from "enums/BookingStatus"
import * as AdvertAvailability from "enums/AdvertAvailability"
import { useBreakpoints } from "hooks/useBreakpoints"
import { WithSeparators } from "components/Separator"
import { ManageContactActions } from "domains/contact/ManageContactActions"
import { ContactForm } from "components/ContactForm"
import { ContactMethods } from "components/ContactMethods"
import * as User from "domains/user/model"
import classNames from "classnames"
import * as SubscriptionType from "enums/SubscriptionType";

export function ProfilePage({
  user,
  tabs,
  children,
  infos,
  advert,
  extraActions,
  sidebarChildren,
}) {
  const connectedUser = useConnectedUser()
  const translator = useTranslator()

  const previousSearchURL = localStorage.getItem("previousSearch")

  return (
    <Page>
      <div className="space-y-12">
        <div
          className={classNames("py-6 lg:py-8", {
            "bg-primary-lighter": !User.isPro(user),
            "bg-primary-default": User.isPro(user),
          })}
        >
          <Wrapper>
            <div className="space-y-8">
              <div className="flex flex-col md:flex-row">
                <div className="md:grow">
                  <UserInfos user={user} variant="full" />
                </div>
                {connectedUser && User.isAdmin(connectedUser) ? (
                  <div className="md:shrink-0">
                    <AdminActions user={user} />
                  </div>
                ) : null}
              </div>
            </div>
          </Wrapper>
        </div>

        <Wrapper>
          <div className="space-y-10">
            {previousSearchURL ? (
              <Button
                href={previousSearchURL}
                variant="text"
                color="secondary"
                iconLeft={<ArrowLeft className="w-4" />}
                onClick={clearPreviousSearch}
              >
                {translator.trans("account.advert.show.back", null, "pages")}
              </Button>
            ) : null}
            <Tabs
              id="profile-page-tabs"
              currentPanel={
                tabs.children.find((tab) => tab.isCurrent)?.name ?? ""
              }
              onChange={() => null}
            >
              <TabList>
                {tabs.children.map((tab) => (
                  <Tab
                    key={tab.name}
                    label={`${tab.name} ${
                      tab.extras.caption !== null
                        ? `(${tab.extras.caption})`
                        : ""
                    }`.trim()}
                    id={tab.name}
                    href={tab.uri}
                  />
                ))}
              </TabList>
            </Tabs>
            <TwoColLayout
              main={children}
              sidebar={
                <Sidebar
                  advert={advert}
                  user={user}
                  extraActions={extraActions}
                  {...infos}
                >
                  {sidebarChildren}
                </Sidebar>
              }
            />
          </div>
          <div className="hidden lg:block">
            <Publicity name="horizontal" />
          </div>
        </Wrapper>
      </div>
    </Page>
  )
}

ProfilePage.propTypes = {
  user: PropTypes.object,
  tabs: PropTypes.object,
  children: PropTypes.node,
}

function clearPreviousSearch() {
  localStorage.removeItem("previousSearch")
}

const AdminActions = ({ user }) => {
  const translator = useTranslator()
  const [showActions, setShowActions] = React.useState(false)
  const router = useRouter()

  return (
    <>
      <div className="flex flex-col">
        <Button
          color="primary"
          iconLeft={<EllipsisH className="w-4" />}
          type="button"
          onClick={() => setShowActions(true)}
        >
          Admin
        </Button>
      </div>
      <Modal
        isOpen={showActions}
        onRequestClose={() => setShowActions(false)}
        title="Admin"
      >
        <ModalContent>
          <div className="flex flex-col space-y-2 md:space-y-4">
            <Button
              variant="outline"
              color="secondary"
              href={{
                route: "app_admin_user_edit",
                params: { userId: user.id },
              }}
              iconLeft={<Pen className="w-4" />}
              target="_blank"
            >
              {translator.trans(
                "ProfilePage.adminMenu.modify",
                null,
                "components",
              )}
            </Button>
            <Button
              variant="outline"
              color="secondary"
              href={{
                route: "app_account_profile_show",
                params: { userId: user.id, _switch: user.email },
              }}
            >
              {translator.trans(
                "ProfilePage.adminMenu.switch",
                null,
                "components",
              )}
            </Button>
            <Button
              variant="outline"
              color="secondary"
              href={{
                route: "app_admin_user_super",
                params: { userId: user.id },
              }}
              iconLeft={<SuperNavigator className="w-4" />}
            >
              {user.super
                ? translator.trans(
                    "ProfilePage.adminMenu.super.remove",
                    null,
                    "components",
                  )
                : translator.trans(
                    "ProfilePage.adminMenu.super.add",
                    null,
                    "components",
                  )}
            </Button>
            <Button
              variant="outline"
              color="secondary"
              href={{
                route: "app_admin_user_admin",
                params: { userId: user.id },
              }}
              iconLeft={<Crown className="w-4" />}
            >
              {user.roles.includes("ROLE_ADMIN")
                ? translator.trans(
                    "ProfilePage.adminMenu.admin.remove",
                    null,
                    "components",
                  )
                : translator.trans(
                    "ProfilePage.adminMenu.admin.add",
                    null,
                    "components",
                  )}
            </Button>

            <ConfirmButtonWithRedirect
              url={router.generate("app_admin_user_premium_add", {
                userId: user.id,
                type: SubscriptionType.VOGWEEK
              })}
              color="secondary"
              iconLeft={<CartPlus className="w-4" />}
              confirmTitle={translator.trans(
                "ProfilePage.adminMenu.premium.confirmTitle",
                null,
                "components",
              )}
              confirmContent={
                <Text tag="p">
                  {translator.trans(
                    "ProfilePage.adminMenu.premium.confirmContent",
                    null,
                    "components",
                  )}
                </Text>
              }
              confirmLabel={translator.trans(
                "ProfilePage.adminMenu.premium.confirmLabel",
                null,
                "components",
              )}
            >
              {translator.trans(
                "ProfilePage.adminMenu.premium.label",
                null,
                "components",
              )}
            </ConfirmButtonWithRedirect>

            <ConfirmButtonWithRedirect
              url={router.generate("app_admin_user_premium_add", {
                userId: user.id,
                type: SubscriptionType.VOILES_ET_VOILIERS
              })}
              color="secondary"
              iconLeft={<CartPlus className="w-4" />}
              confirmTitle={translator.trans(
                "ProfilePage.adminMenu.premium.confirmTitle",
                null,
                "components",
              )}
              confirmContent={
                <Text tag="p">
                  {translator.trans(
                    "ProfilePage.adminMenu.premium.confirmContent",
                    null,
                    "components",
                  )}
                </Text>
              }
              confirmLabel={translator.trans(
                "ProfilePage.adminMenu.premium.confirmLabel",
                null,
                "components",
              )}
            >
              {translator.trans(
                "ProfilePage.adminMenu.premium.labelVV",
                null,
                "components",
              )}
            </ConfirmButtonWithRedirect>

            <Button
              variant="outline"
              color="secondary"
              href={{
                route: "app_admin_user_trial_add",
                params: { userId: user.id },
              }}
              iconLeft={<Gift className="w-4" />}
            >
              {translator.trans(
                "ProfilePage.adminMenu.trial",
                null,
                "components",
              )}
            </Button>
            <UnsubscribeButton user={user} />
            <TerminateSubscriptionButton user={user} />
            <DeleteProfileButton user={user} />
          </div>
        </ModalContent>
      </Modal>
    </>
  )
}

function DeleteProfileButton({ user }) {
  const router = useRouter()
  const translator = useTranslator()

  return (
    <ConfirmButtonWithRedirect
      url={router.generate("app_admin_user_delete", { userId: user.id })}
      iconLeft={<TrashAlt className="w-4" />}
      color="danger"
      confirmTitle={translator.trans(
        "ProfilePage.adminMenu.deleteProfile.confirmTitle",
        null,
        "components",
      )}
      confirmContent={
        <Text tag="p">
          {translator.trans(
            "ProfilePage.adminMenu.deleteProfile.confirmContent",
            null,
            "components",
          )}
        </Text>
      }
      confirmLabel={translator.trans(
        "ProfilePage.adminMenu.deleteProfile.confirmLabel",
        null,
        "components",
      )}
    >
      {translator.trans(
        "ProfilePage.adminMenu.deleteProfile.confirmTitle",
        null,
        "components",
      )}
    </ConfirmButtonWithRedirect>
  )
}

function UnsubscribeButton({ user }) {
  const router = useRouter()
  const translator = useTranslator()

  return (
    <ConfirmButtonWithRedirect
      url={router.generate("app_admin_user_newsletter_unsubcribe", {
        userId: user.id,
      })}
      color="danger"
      iconLeft={<Envelope className="w-4" />}
      confirmTitle={translator.trans(
        "ProfilePage.adminMenu.unsubscribeProfile.confirmTitle",
        null,
        "components",
      )}
      confirmContent={
        <Text tag="p">
          {translator.trans(
            "ProfilePage.adminMenu.unsubscribeProfile.confirmContent",
            null,
            "components",
          )}
        </Text>
      }
      confirmLabel="Désinscrire"
    >
      {translator.trans(
        "ProfilePage.adminMenu.unsubscribeProfile.confirmTitle",
        null,
        "components",
      )}
    </ConfirmButtonWithRedirect>
  )
}

function TerminateSubscriptionButton({ user }) {
  const router = useRouter()
  const translator = useTranslator()

  return (
    <ConfirmButtonWithRedirect
      color="danger"
      url={router.generate("app_admin_user_subscription_terminate", {
        userId: user.id,
      })}
      iconLeft={<UserTimes className="w-4" />}
      confirmTitle={translator.trans(
        "ProfilePage.adminMenu.terminateSubscription.confirmTitle",
        null,
        "components",
      )}
      confirmContent={
        <Text tag="p">
          {translator.trans(
            "ProfilePage.adminMenu.terminateSubscription.confirmContent",
            null,
            "components",
          )}
        </Text>
      }
      confirmLabel={translator.trans(
        "ProfilePage.adminMenu.terminateSubscription.confirmLabel",
        null,
        "components",
      )}
    >
      {translator.trans(
        "ProfilePage.adminMenu.terminateSubscription.confirmTitle",
        null,
        "components",
      )}
    </ConfirmButtonWithRedirect>
  )
}

const ConfirmButtonWithRedirect = ({
  color,
  url,
  confirmTitle,
  confirmContent,
  confirmLabel,
  iconLeft,
  children,
}) => {
  const router = useRouter()
  const [isLoading, setIsLoading] = React.useState(false)

  const handleConfirm = () => {
    setIsLoading(true)

    router.redirect(url)
  }

  return (
    <ConfirmButton
      variant="outline"
      color={color}
      confirmTitle={confirmTitle}
      confirmContent={confirmContent}
      confirmLabel={confirmLabel}
      iconLeft={iconLeft}
      busy={isLoading}
      onConfirm={handleConfirm}
    >
      {children}
    </ConfirmButton>
  )
}

const Sidebar = ({
  addReviewUrl,
  advert,
  children,
  extraActions = [],
  formBooking,
  formMessage,
  preparedMessages,
  require,
  user,
}) => {
  const modalsStack = useStack()
  const translator = useTranslator()
  const connectedUser = useConnectedUser()
  const isOwnProfile = connectedUser?.id === user.id

  const shouldShowBookingButton =
    !isOwnProfile && (formBooking || require.formBooking)
  const shouldShowContactButton = !isOwnProfile
  const shouldShowManageContactButton = !isOwnProfile
  const shouldShowReviewButton =
    !isOwnProfile && (addReviewUrl || require.addReviewUrl)

  const contact = connectedUser
    ? getContactBetweenUsers(connectedUser, user)
    : null

  const bookingButton = shouldShowBookingButton ? (
    <BookingButton
      key="booking"
      onClick={() => modalsStack.push("form-booking")}
      shouldRequireClub={require.formBooking}
    />
  ) : null

  const reviewButton = shouldShowReviewButton ? (
    <AddReviewButton
      key="review"
      href={addReviewUrl}
      shouldRequireClub={require.addReviewUrl}
    />
  ) : null

  const manageContactActions = <ManageContactActions user={user} />

  const contactMethods = (
    <ContactMethods
      user={user}
      advert={advert}
      shouldRequireClub={require.formMessage}
      canSendDirectMessage={
        Boolean(require.formMessage) && !shouldShowBookingButton
      }
      onDirectMessageClick={() => modalsStack.push("form-message")}
    />
  )

  const actions = [
    bookingButton,
    shouldShowContactButton ? (
      <Button
        key="contact"
        variant="outline"
        color="secondary"
        iconLeft={<Comments className="w-4" />}
        onClick={() => modalsStack.push("contact-methods")}
      >
        {translator.trans("ProfilePage.actions.contact", null, "components")}
      </Button>
    ) : null,
    reviewButton,
    shouldShowManageContactButton ? (
      <Button
        key="manage-contact"
        variant="outline"
        color="secondary"
        iconLeft={
          <ContactTypeIcon
            contactType={contact ? contact.type : null}
            className="w-4"
          />
        }
        onClick={() => modalsStack.push("manage-contact")}
      >
        {translator.trans(
          "ProfilePage.actions.manageContact",
          null,
          "components",
        )}
      </Button>
    ) : null,

    ...extraActions,
  ].filter(Boolean)

  const [mainAction, ...secondaryActions] = actions

  const desktopBlocks = [
    bookingButton
      ? React.cloneElement(bookingButton, { key: "booking" })
      : null,
    shouldShowContactButton
      ? React.cloneElement(contactMethods, { key: "contact-methods" })
      : null,
    reviewButton ? React.cloneElement(reviewButton, { key: "review" }) : null,
    !isOwnProfile
      ? React.cloneElement(manageContactActions, { key: "manage-contact" })
      : null,

    extraActions.length > 0 ? (
      <div className="flex flex-col space-y-4" key="extra-actions">
        {extraActions}
      </div>
    ) : null,
    children,
  ].filter(Boolean)

  const { lg } = useBreakpoints()

  if (!lg && actions.length === 0) {
    return null
  }

  return (
    <div>
      <div className="flex space-x-2 lg:hidden">
        <div className="flex flex-col grow">{mainAction}</div>
        {secondaryActions.length > 0 ? (
          <div className="shrink-0">
            <Button
              variant="outline"
              color="secondary"
              iconLeft={<EllipsisH className="w-4" />}
              type="button"
              onClick={() => modalsStack.push("secondary-actions")}
            />
          </div>
        ) : null}
      </div>
      <div className="hidden flex-col space-y-4 lg:flex">
        <WithSeparators>{desktopBlocks}</WithSeparators>
      </div>
      {formBooking ? (
        <Modal
          isOpen={modalsStack.head === "form-booking"}
          onRequestClose={modalsStack.pop}
          title={translator.trans(
            "ProfilePage.formBooking.title",
            { firstName: user.firstName },
            "components",
          )}
        >
          <ModalContent>
            <BookingForm
              form={formBooking}
              onSuccess={modalsStack.empty}
              user={user}
              advert={advert}
              preparedMessages={preparedMessages}
            />
          </ModalContent>
        </Modal>
      ) : null}

      {formMessage ? (
        <Modal
          isOpen={modalsStack.head === "form-message"}
          onRequestClose={modalsStack.pop}
          title={translator.trans(
            "ProfilePage.formMessage.title",
            { name: user.fullName },
            "components",
          )}
        >
          <ModalContent>
            <ContactForm
              form={formMessage}
              onSuccess={modalsStack.empty}
              preparedMessages={preparedMessages}
              messageFieldName="content"
              submitLabel={translator.trans(
                "ProfilePage.formMessage.submitLabel",
                null,
                "components",
              )}
            />
          </ModalContent>
        </Modal>
      ) : null}

      <Modal
        isOpen={modalsStack.head === "manage-contact"}
        onRequestClose={modalsStack.pop}
        title={translator.trans(
          "ProfilePage.manageContact.title",
          null,
          "components",
        )}
      >
        <ModalContent>{manageContactActions}</ModalContent>
      </Modal>

      <Modal
        isOpen={modalsStack.head === "contact-methods"}
        onRequestClose={modalsStack.pop}
        title={translator.trans(
          "ProfilePage.contactMethods.title",
          null,
          "components",
        )}
      >
        <ModalContent>{contactMethods}</ModalContent>
      </Modal>

      <Modal
        isOpen={modalsStack.head === "secondary-actions"}
        onRequestClose={modalsStack.pop}
        title={translator.trans(
          "ProfilePage.secondaryActions",
          null,
          "components",
        )}
      >
        <ModalContent>
          <div className="flex flex-col space-y-4">{secondaryActions}</div>
        </ModalContent>
      </Modal>
    </div>
  )
}

const useStack = () => {
  const [values, setValues] = React.useState([])

  const push = (value) => setValues((prevValues) => [...prevValues, value])
  const pop = () => setValues((prevValues) => prevValues.slice(0, -1))
  const empty = () => setValues([])

  const head = values[values.length - 1] ?? null

  return { push, pop, empty, head }
}

const BookingButton = ({ onClick, shouldRequireClub }) => {
  const translator = useTranslator()

  const button = (
    <Button
      iconLeft={<CalendarCheckRegular className="w-4" />}
      onClick={onClick}
      color="secondary"
    >
      {translator.trans("ProfilePage.actions.booking", null, "components")}
    </Button>
  )

  if (shouldRequireClub) {
    return <RequireClub>{button}</RequireClub>
  }

  return button
}

const AddReviewButton = ({ href, shouldRequireClub }) => {
  const translator = useTranslator()

  const button = (
    <Button href={href || "#"} iconLeft={<PlusSquareRegular className="w-4" />}>
      {translator.trans("ProfilePage.actions.review", null, "components")}
    </Button>
  )

  if (shouldRequireClub) {
    return <RequireClub includeExClub={true}>{button}</RequireClub>
  }

  return button
}

const BookingForm = ({ form, onSuccess, user, advert, preparedMessages }) => {
  const connectedUser = useConnectedUser()

  const entity = {
    status: BookingStatus.CONFIRMED,
    nbParticipants: 1,
    booking: {
      advert: advert?.masterAdvertId ?? advert?.id,
      subAdvert: advert?.masterAdvertId ? advert?.id : null,
      creatorId: connectedUser?.id,
      user: connectedUser?.id,
      status: BookingStatus.CONFIRMED,
      nbParticipants: 1,
    },
  }

  if (
    [AdvertAvailability.DATES, AdvertAvailability.EVENT].includes(
      advert.availability?.enum_value,
    )
  ) {
    entity.booking.dateInterval = {
      from: advert.departureDate,
      to: advert.arrivalDate,
    }
    entity.dateInterval = {
      from: advert.departureDate,
      to: advert.arrivalDate,
    }
  }

  const translator = useTranslator()

  return (
    <ContactForm
      form={form}
      onSuccess={onSuccess}
      user={user}
      entity={entity}
      preparedMessages={preparedMessages}
      messageFieldName="content"
      submitLabel={translator.trans(
        "ProfilePage.formMessage.submitLabel",
        null,
        "components",
      )}
    />
  )
}
